<template>
  <div class="slider--section">
    <div class="slider__wrapper">
      <div class="slider__inner">
        <div
          v-for="(slide, index) in props.slider"
          :key="slide?._uid || index"
          :class="['slider__slide', handleCssClassForItemWidth]"
        >
          <slot :slide="slide" :index="index"></slot>
        </div>
      </div>
    </div>
    <div
      v-if="isNavVisible || viewportMobile"
      class="slider__indicator"
    >
      <span
        v-for="(_, index) in getBullets"
        :key="index"
        :class="['slider__indicator--bullet', { active: setBulletActive(index) }]"
        @click="scrollSlider(index)"
      ></span>
    </div>
    <div
      v-if="isNavVisible"
      :class="['slider__navigation', { 'visible-prev': !isPrevVisible }]"
    >
      <PflegeButton
        v-if="isPrevVisible"
        variant="text"
        icon="chevron-left-line"
        class="prev"
        @click.prevent="indexOfActiveBullet > 0 ? scrollSlider(indexOfActiveBullet - 1) : null"
      />
      <PflegeButton
        v-if="isNextVisible"
        variant="text"
        icon="chevron-right-line"
        class="next"
        @click.prevent="(indexOfActiveBullet + 1) < getBullets.length ? scrollSlider(indexOfActiveBullet + 1) : null"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { ViewportElements, ViewPorts } from '../../../constants/enums'
import PflegeButton from '../Button'

const props = defineProps({
  slider: {
    type: Array,
    required: true,
    default: () => {
      return []
    },
  },
})

const indexOfActiveBullet = ref(0)
const windowWidth = ref(0)
const isNextVisible = ref(true)
const isPrevVisible = ref(true)

const { desktop, tablet } = ViewportElements
const { breakpointL, breakpointM } = ViewPorts

const viewportDesktop = computed(() => windowWidth.value >= breakpointL)
const viewportTablet = computed(() => windowWidth.value >= breakpointM && windowWidth.value < breakpointL)
const viewportMobile = computed(() => !viewportDesktop.value && !viewportTablet.value)
const sliderItemsLength = computed(() => props.slider.length)
const handleCssClassForItemWidth = computed(() => {
  if (sliderItemsLength.value > 1) {
    return 'carousel'
  } else {
    return ''
  }
})
const sliderWrapper = computed(() => document.querySelector('.slider__inner'))
const getBullets = computed(() => {
  return new Array(
    Math.ceil(viewportDesktop.value
      ? sliderItemsLength.value / desktop
      : viewportTablet.value
        ?  sliderItemsLength.value / tablet
        : sliderItemsLength.value,
    ))
})
const isNavVisible = computed(() => {
  return viewportTablet.value && props.slider.length > 2
    || viewportDesktop.value && props.slider.length > 3
})
const slideList = computed(() => document.querySelectorAll('.slider__slide'))

const setBulletActive = (switchBullet) => {
  return indexOfActiveBullet.value === switchBullet
}

const scrollSlider =  (value) => {
  const { scrollWidth } =  slideList.value[value]
  const getScrollLeftFactor = viewportDesktop.value
    ? scrollWidth * desktop
    : viewportTablet.value
      ? scrollWidth * tablet
      : scrollWidth

  sliderWrapper.value.scrollLeft = getScrollLeftFactor * value

  if (value !== indexOfActiveBullet.value) {
    setTimeout(() => {
      indexOfActiveBullet.value = value
    }, 500)
  }
}

const setWindowWidth = () => {
  return windowWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', setWindowWidth)
  setWindowWidth()

  const observer = new IntersectionObserver(
    (entries) => {
      for(const entry of entries) {
        if (entry.isIntersecting) {
          const currentIndex = Array.from(slideList.value).indexOf(entry.target)
          const slideLastChildIndex = slideList.value.length - 1

          if (slideLastChildIndex === currentIndex) {
            isNextVisible.value = false
          } else {
            isNextVisible.value = true
          }

          if (currentIndex === 0) {
            isPrevVisible.value = false
          } else {
            isPrevVisible.value = true
          }

          const isInViewElementOnPositionLeft = (view) => {
            return currentIndex % view === view - 1
              || currentIndex === slideLastChildIndex
          }

          if (currentIndex !== indexOfActiveBullet.value && !viewportDesktop.value && !viewportTablet.value) {
            indexOfActiveBullet.value = currentIndex
          } else if (viewportDesktop.value && isInViewElementOnPositionLeft(desktop)) {
            indexOfActiveBullet.value = Math.floor(currentIndex  / desktop)
          } else if (viewportTablet.value && isInViewElementOnPositionLeft(tablet)) {
            indexOfActiveBullet.value = Math.floor(currentIndex  / tablet)
          } else {
            return
          }
        }
      }
    },
    {
      threshold: 0.5,
    },
  )

  slideList.value.forEach((element) => observer.observe(element))
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', setWindowWidth)
})
</script>

<style lang="scss" scoped>
@use '../../../styles' as index;

.slider {
  &--section {
    position: relative;
    overflow-x: visible;
    margin-inline: calc(1.25rem * -1);

    @media screen and (min-width: index.$breakpoint--m) {
      margin-inline: calc(0.5rem * -1);
    }
  }

  &__wrapper {
    width: 100%;
    position: relative;
    max-width: 100%;
    overflow: hidden;
  }

  &__inner {
    display: flex;
    position: relative;
    margin-inline: auto;
    width: 100%;
    max-width: 100%;
    overflow-x: scroll;
    scroll-behavior: smooth;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;

    @media screen and (max-width: index.$breakpoint--m) {
      padding-inline: 1rem;
      scroll-padding: 1rem;
    }
  }

  &__slide {
    display: flex;
    flex: 0 0 100%;
    overflow-x: clip;
    max-width: 1000%;
    padding-inline: 0.25rem;

    @media screen and (min-width: index.$breakpoint--m) {
      padding-inline: 0.5rem;
    }

    &.carousel {
      @mixin viewportElements($i) {
        flex-basis: calc(100% / #{$i});
        max-width: calc(100% / #{$i});

        &:nth-of-type(#{$i}n + 1) {
          scroll-snap-stop: always;
          scroll-snap-align: start;
        }
      }

      @media screen and (max-width: index.$breakpoint--m) {
        flex-basis: 90%;
        max-width: 90%;
        scroll-snap-stop: always;
        scroll-snap-align: center;
      }

      @media screen and (min-width: index.$breakpoint--m) and (max-width: index.$breakpoint--l) {
        @include viewportElements(2)
      }

      @media screen and (min-width: index.$breakpoint--l) {
        @include viewportElements(3)
      }
    }
  }

  &__navigation {
    display: flex;
    opacity: 0;
    position: absolute;
    width: 100%;
    padding-inline: 0.5rem;
    justify-content: space-between;
    top: 50%;
    left: 0;
    transform: translateY(calc(-50% - 15px));
    z-index: 10;
    pointer-events: none;

    @media screen and (min-width: index.$breakpoint--l) {
      width: calc(100% + 6rem);
      opacity: 1;
      transform: translate(-3rem, calc(-50% - 15px));
    }

    &.visible-prev {
      justify-content: flex-end;
    }

    .slider--section:hover & {
      opacity: 1;
      transition: opacity var(--time2) ease-in-out;
    }

    &:deep(.prev),
    &:deep(.next) {
      cursor: pointer;
      pointer-events: all;
      background-color: transparent;

      &:is(:focus, :active, :hover) {
        background-color: transparent;
        box-shadow: none;
        border: 0 none;
      }

      &:hover {
        background-color: #ed6976;
        color: var(--white);
        opacity: 0.75;
      }
    }

    &:deep(.prev) {
      @media screen and (min-width: index.$breakpoint--m) {
        transform: translateX(-25%);
      }
    }

    &:deep(.next) {
      @media screen and (min-width: index.$breakpoint--m) {
        transform: translateX(25%);
      }
    }
  }

  &__indicator {
    display: flex;
    justify-content: center;
    margin-block: 1rem;

    &--bullet {
      display: inline-flex;
      margin-inline: 0.25rem;
      width: 0.8rem;
      height: 0.8rem;
      background-color: var(--blue-lightest);
      box-sizing: content-box;
      border-radius: 50%;
      cursor: pointer;

      &.active {
        background-color: var(--blue-light);
        transition: background-color var(--time2) ease-in-out;
      }
    }
  }
}

::-webkit-scrollbar {
  display: none;
}
</style>
